import { fitRoutineNodeOrbit } from '../../../../engine/core/routine-orbit.js';
import { appendBadge as appendRoutineBadge, isRoutine } from '../../ui/routines.js';
/**
 * Mindmap View - Render Module
 *
 * Node creation, SVG path drawing, breadcrumbs.
 * Uses callback injection pattern for dependencies.
 */

import * as State from './state.js';
import { renderHorizonControl } from '../../ui/horizon-control.js';
import * as MmCanvas from './canvas-renderer.js';
import * as Layout from './layout.js';
import * as Tidy from './tidy-layout.js';
import * as Interaction from './interaction.js';
import { setupNotePreviewOnBadge } from '../../ui/note-preview.js';
import * as Store from '../../store/index.js';
import { escapeAttr, safeColor } from '../../../../engine/core/validation.js';
import { getLinkedBadge, linkedStubContainerClasses } from '../linked-badge.js';
import { isMobile } from '../../../../shared/utils/device-detect.js';
import { t } from '../../../../shared/i18n/index.js';
import { viewLabel } from '../../../../engine/core/view-names.js';

// ============================================================
// Module callbacks (injected via init)
// ============================================================
let renderCallbacks = null;

/**
 * Initialize render module with callbacks
 * @param {Object} cbs
 * @param {Function} cbs.find - Find node by ID
 * @param {Function} cbs.findParent - Find parent of node
 * @param {Function} cbs.onNodeUpdate - Update node
 * @param {Function} cbs.onNodeSelect - Select node
 * @param {Function} cbs.onNodeEdit - Edit node inline
 * @param {Function} cbs.onNodeContextMenu - Show context menu
 * @param {Function} cbs.onOpenModal - Open properties modal
 * @param {Function} cbs.onNavigate - Navigate to node
 * @param {Function} cbs.isReadOnly - Check read-only status
 * @param {Function} cbs.escapeHtml - HTML escape helper
 * @param {Function} cbs.beginBatch - Start CRDT batch
 * @param {Function} cbs.endBatch - End CRDT batch
 * @param {Function} cbs.setupDrag - Setup drag on node (from drag-drop.js)
 * @param {Function} cbs.zoomTo - Zoom to node (from zoom-pan.js)
 * @param {Function} cbs.updateTransform - Update transform (from zoom-pan.js)
 */
export function init(cbs) {
  renderCallbacks = cbs;
}

// Set of node ids that have a tag-match somewhere in their subtree but are
// NOT themselves a tag-match. Rebuilt at the start of each render.
const tagMatchAncestorIds = new Set();

/**
 * Walk the tree and populate `tagMatchAncestorIds` with non-matching nodes
 * whose subtrees contain a matching node. Used at create-time to apply
 * `.tag-match-ancestor` so the dim overlay doesn't block the visual path
 * down to a matching descendant.
 */
function rebuildTagMatchAncestors(root, highlightedTagIds) {
  tagMatchAncestorIds.clear();
  if (!root || !highlightedTagIds?.length) return;
  const isMatch = (n) => !!State.getTagFilter()?.isMatch(n, highlightedTagIds);
  const visit = (n) => {
    let any = isMatch(n);
    for (const c of n.children || []) {
      if (visit(c)) any = true;
    }
    if (any && !isMatch(n)) tagMatchAncestorIds.add(n.id);
    return any;
  };
  visit(root);
}

// DOM mode: node elements and edges kept between renders, so a render
// rebuilds only the nodes whose content changed and moves the others
// (rebuilding and measuring every node took about 1.5 s per edit at 20,000
// items: docs/internal/perf/2026-10-08-big-maps.md). A kept element's
// listeners read the current node from `el._mmNode`.
const kept = { world: null, defs: null, nodes: new Map(), edges: new Map(), pass: 0 };

function dropKept(world) {
  kept.world = world;
  kept.defs = null;
  kept.nodes = new Map();
  kept.edges = new Map();
}

/** What a node element shows (createMmNode's inputs): when any of it
 * changes, the element is built again. */
function nodeLook(node, isRoot, direction, context) {
  const hasChildren = node.children?.length > 0;
  const linked = getLinkedBadge(node);
  return [isRoot, hasChildren, !!(hasChildren && node.collapsed), node.name || node.text || "", node.color,
    State.callbacks?.getEffectiveStatus?.(node) || node.status || "", isRoot ? "" : direction || "",
    linked.isLinkedStub ? `${linked.className}|${linked.icon}|${linked.title}|${linked.state}|${linkedStubContainerClasses(node) || ""}` : "",
    getSpotlightState(node) || "", isRoot ? "" : node.notes || "", context.isViewer, context.docId, context.dek];
}

function sameLook(a, b) {
  if (!b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/** The node's element: the kept one when it shows the same, else a new one
 * (replacing the kept one). */
function keptNode(node, isRoot, direction, context) {
  const look = nodeLook(node, isRoot, direction, context);
  const entry = kept.nodes.get(node.id);
  if (entry && entry.pass === kept.pass) {
    // An id shown twice (never expected): the second gets its own element,
    // removed by the next render.
    const el = createMmNode(node, 0, 0, isRoot, direction);
    State.world.appendChild(el);
    return el;
  }
  if (entry && sameLook(look, entry.look) && entry.el.parentNode === State.world) {
    entry.pass = kept.pass;
    entry.el._mmNode = node;
    return entry.el;
  }
  const el = createMmNode(node, 0, 0, isRoot, direction);
  if (entry?.el.parentNode === State.world) entry.el.replaceWith(el);
  else State.world.appendChild(el);
  kept.nodes.set(node.id, { el, look, pass: kept.pass });
  return el;
}

/** The edge to `node` from its parent: kept when it is drawn the same
 * (dragging redraws edges in place: the path is compared as drawn). */
function keptEdge(defs, px, py, x, y, node) {
  const d = edgePath(px, py, x, y);
  const entry = kept.edges.get(node.id);
  if (entry && entry.pass !== kept.pass && entry.color === node.color
      && entry.path.parentNode === State.svg && entry.path.getAttribute("d") === d) {
    entry.pass = kept.pass;
    return;
  }
  if (entry && entry.pass !== kept.pass) {
    entry.path.remove();
    entry.gradient.remove();
  }
  const edge = createDomEdge(defs, px, py, x, y, node);
  if (!entry || entry.pass !== kept.pass) kept.edges.set(node.id, { ...edge, color: node.color, pass: kept.pass });
}

/** Remove what this render did not show, and anything not kept. */
function pruneKept() {
  for (const [id, entry] of kept.nodes) {
    if (entry.pass !== kept.pass) {
      entry.el.remove();
      kept.nodes.delete(id);
    }
  }
  for (const [id, entry] of kept.edges) {
    if (entry.pass !== kept.pass) {
      entry.path.remove();
      entry.gradient.remove();
      kept.edges.delete(id);
    }
  }
  // Strays (elements this map did not keep): looked for only when the
  // counts differ, as each check reads an id (27,000 nodes: 0.2 s).
  let nodeCount = 0;
  for (const el of State.world.children) if (el.classList.contains("mm-node")) nodeCount++;
  if (nodeCount !== kept.nodes.size) {
    for (const el of [...State.world.children]) {
      if (el.classList.contains("mm-node") && kept.nodes.get(el.dataset.id)?.el !== el) el.remove();
    }
  }
  if (State.svg.childElementCount !== kept.edges.size + 1) {
    for (const el of [...State.svg.children]) {
      if (el !== kept.defs && kept.edges.get(el.id.slice(5))?.path !== el) el.remove();
    }
  }
  if (kept.defs.childElementCount !== kept.edges.size) {
    for (const el of [...kept.defs.children]) {
      if (kept.edges.get(el.id.slice(9))?.gradient !== el) el.remove();
    }
  }
}

// ============================================================
// Render Scheduling
// ============================================================

/**
 * Schedule a render for the next animation frame (batches multiple requests)
 */
export function scheduleRender() {
  if (State.renderScheduled) return;
  // Skip render during drag - node positions are being controlled by mouse
  if (State.isDraggingNode) {
    console.log("[Mindmap] Skipping render during drag");
    return;
  }
  State.setRenderScheduled(true);

  requestAnimationFrame(() => {
    State.setRenderScheduled(false);
    doRender();
  });
}

/**
 * Render the mindmap - public API that schedules a batched render
 */
export function renderMindmap() {
  scheduleRender();
}

/** Draw every node and edge again, keeping none (check-mindmap-render
 * compares this with the kept ones). */
export function renderFromScratch() {
  dropKept(null);
  doRender();
}

// ============================================================
// Main Render Function
// ============================================================

/**
 * Actually render the mindmap for the focused node
 */
function doRender() {
  if (!State.world || !State.svg) return;

  // Suppress dimension logs during render to avoid flooding console
  State.setSuppressDimensionLogs(true);

  // Canvas mode (mobile) builds no elements: none kept. DOM mode keeps them
  // while the view keeps its world element.
  const canvasMode = MmCanvas.isMounted();
  if (canvasMode || kept.world !== State.world) {
    State.world.querySelectorAll(".mm-node").forEach((n) => n.remove());
    State.svg.innerHTML = "";
    dropKept(canvasMode ? null : State.world);
  }
  kept.pass++;

  // Tag spotlight mode: toggle class on world container
  const highlightedTagIds = State.callbacks?.getHighlightedTagIds?.() || [];
  State.world.classList.toggle('tag-filtering', highlightedTagIds.length > 0);

  // Reset memoized visibility for this render (hide-filter settings or tree
  // structure may have changed since last call).
  State.clearVisibilityCache();
  // Precompute ancestors of tag-matching nodes — they get .tag-match-ancestor
  // so the dim overlay doesn't hide the path down to a matching descendant.
  rebuildTagMatchAncestors(State.currentState?.data, highlightedTagIds);

  if (!kept.defs || kept.defs.parentNode !== State.svg) {
    kept.defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
    State.svg.prepend(kept.defs);
  }
  const defs = kept.defs;

  const focused = State.callbacks?.find(State.currentState?.focusId) || State.currentState?.data;
  if (!focused) {
    if (!canvasMode) pruneKept(); // nothing shown
    return;
  }

  renderBreadcrumbs();

  const rect = State.canvas?.getBoundingClientRect();

  // If canvas doesn't have valid dimensions yet (e.g., during initial load),
  // defer rendering until it does
  if (!rect || rect.width < 100 || rect.height < 100) {
    requestAnimationFrame(() => doRender());
    return;
  }

  const rootX = rect.width / 2;
  const rootY = rect.height / 2;

  // Canvas mode (mobile): the SAME layout pass runs, but nodes and edges
  // become paint records instead of DOM. See canvas-renderer.js.
  const sceneNodes = [];
  const context = { isViewer: State.callbacks?.isReadOnly?.(), docId: State.callbacks?.getDocId?.(), dek: State.callbacks?.getDek?.() };
  const sceneEdges = [];

  // Sides of the focused node's children come from ALL of them (hidden ones
  // included), so filtering, collapsing or growing never moves a branch across.
  const sides = Tidy.assignSides(focused.children || [], State.layoutMode || "balanced");

  // 1. Build every visible node at the origin (all DOM writes first).
  const built = new Map(); // id -> { node, item, kids }
  const build = (node, isRoot, direction) => {
    const item = canvasMode
      ? collectCanvasNode(node, 0, 0, isRoot, direction, sceneNodes)
      : keptNode(node, isRoot, direction, context);
    const kids = node.children?.length && !node.collapsed ? State.getVisibleChildren(node) : [];
    built.set(node.id, { node, item, kids });
    for (const child of kids) build(child, false, isRoot ? sides.get(child.id) : direction);
  };
  build(focused, true, null);

  // 2. Measure the rendered sizes (one layout pass), 3. lay the tree out from
  // them. Nothing is stored: positions are a pure function of the visible
  // tree, the sizes and the user's choices (side, dragged distance), so every
  // client computes the same map and nothing goes stale.
  const sizes = new Map();
  for (const [id, { node, item }] of built) {
    const size = canvasMode ? canvasLayoutSize(item) : domLayoutSize(item, node);
    sizes.set(id, size);
    if (!canvasMode) item._mmLayoutSize = size;
  }
  const positions = Tidy.computeTreeLayout(focused, {
    size: (node) => sizes.get(node.id),
    children: (node) => built.get(node.id).kids,
    sideOf: (node) => sides.get(node.id),
    vGap: Layout.getVerticalGap(),
    subtreeGap: Layout.getSiblingSubtreeGap(),
    extraX: (node, level) => Tidy.extraFromOffset(node.mmOffsetX, level),
  });

  // 4. Place nodes and draw edges.
  for (const [id, pos] of positions) {
    const { node, item } = built.get(id);
    const x = rootX + pos.x;
    const y = rootY + pos.y;
    if (canvasMode) {
      item.x = x;
      item.y = y;
    } else {
      const left = `${x}px`;
      const top = `${y}px`;
      if (item.style.left !== left) item.style.left = left;
      if (item.style.top !== top) item.style.top = top;
    }
    const parent = pos.parentId && positions.get(pos.parentId);
    if (!parent) continue;
    const px = rootX + parent.x;
    const py = rootY + parent.y;
    if (canvasMode) {
      sceneEdges.push({ px, py, x, y, color: node.color, dim: getSpotlightState(node) === "dim" });
    } else {
      keptEdge(defs, px, py, x, y, node);
    }
  }

  if (canvasMode) MmCanvas.setScene({ nodes: sceneNodes, edges: sceneEdges });
  else {
    pruneKept();
    fitRoutineArrows(built);
  }

  renderCallbacks?.updateTransform?.();

  // Re-enable dimension logs after render
  State.setSuppressDimensionLogs(false);
}

// Room kept around a routine node for its orbit arrow (ROUTINE_ORBIT_GAP plus
// the arrow head), so neighbours never touch the ring.
const ROUTINE_ORBIT_CLEARANCE = 15;

function domLayoutSize(el, node) {
  const pad = isRoutine(node.id) ? 2 * ROUTINE_ORBIT_CLEARANCE : 0;
  el._mmBox = { w: el.offsetWidth, h: el.offsetHeight };
  return { w: el._mmBox.w + pad, h: el._mmBox.h + pad };
}

function canvasLayoutSize(rec) {
  const pad = rec.routine ? 2 * ROUTINE_ORBIT_CLEARANCE : 0;
  return { w: rec.bodyW + pad, h: rec.bodyH + pad };
}

// Fit each routine node's orbit to the size measured for the layout (a node
// is max-content wide: placing it does not change its size), so placing the
// nodes needs no second layout pass.
function fitRoutineArrows(built) {
  for (const { item } of built.values()) {
    if (!item.classList.contains('routine-node')) continue;
    fitRoutineNodeOrbit(item.querySelector(':scope > .routine-badge'), item._mmBox.w, item._mmBox.h, 25);
  }
}

// ============================================================
// Node Creation
// ============================================================

/**
 * Create a mindmap node element
 */
/**
 * Tag-spotlight classification as data ('match' | 'ancestor' | 'dim' | null).
 * The DOM path expresses this via CSS classes; the canvas path reads it
 * directly to dim non-matching nodes and edges.
 */
function getSpotlightState(node) {
  const highlightedTagIds = State.callbacks?.getHighlightedTagIds?.() || [];
  if (!highlightedTagIds.length) return null;
  if (State.getTagFilter()?.isMatch(node, highlightedTagIds)) return "match";
  return tagMatchAncestorIds.has(node.id) ? "ancestor" : "dim";
}

/**
 * DOM edge: gradient-stroked bezier from parent to child, appended to the
 * shared SVG. (Canvas mode collects edge records instead; see doRender.)
 */
function edgePath(parentX, parentY, finalX, finalY) {
  const cpx = parentX + (finalX - parentX) * 0.5;
  return `M${parentX},${parentY} C${cpx},${parentY} ${cpx},${finalY} ${finalX},${finalY}`;
}

function createDomEdge(defs, parentX, parentY, finalX, finalY, node) {
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");

  const gradientId = `gradient-${node.id}`;
  const gradient = document.createElementNS("http://www.w3.org/2000/svg", "linearGradient");
  gradient.setAttribute("id", gradientId);
  gradient.setAttribute("gradientUnits", "userSpaceOnUse");
  gradient.setAttribute("x1", parentX);
  gradient.setAttribute("y1", parentY);
  gradient.setAttribute("x2", finalX);
  gradient.setAttribute("y2", finalY);

  const stop1 = document.createElementNS("http://www.w3.org/2000/svg", "stop");
  stop1.setAttribute("offset", "0%");
  stop1.setAttribute("stop-color", node.color);
  stop1.setAttribute("stop-opacity", "0.6");

  const stop2 = document.createElementNS("http://www.w3.org/2000/svg", "stop");
  stop2.setAttribute("offset", "100%");
  stop2.setAttribute("stop-color", node.color);
  stop2.setAttribute("stop-opacity", "1");

  gradient.appendChild(stop1);
  gradient.appendChild(stop2);
  defs.appendChild(gradient);

  path.setAttribute("d", edgePath(parentX, parentY, finalX, finalY));
  path.setAttribute("stroke", `url(#${gradientId})`);
  path.setAttribute("class", "mm-path");
  path.setAttribute("id", `path-${node.id}`);
  State.svg.appendChild(path);
  return { path, gradient };
}

/**
 * Canvas scene collector — the canvas-mode counterpart of createMmNode.
 * Runs the same dimension logic and pushes a paint record instead of building
 * an element; doRender sets the record's x/y once the layout is known.
 */
function collectCanvasNode(node, x, y, isRoot, direction, sceneNodes) {
  const hasChildren = node.children?.length > 0;
  const isLeaf = !hasChildren;

  const nodeText = (node.name || node.text || "").slice(0, 1000);
  const footprint = Layout.calculateNodeDimensions(nodeText, isRoot, isLeaf, node.id);
  const dims = Layout.calculateNodeDimensions(nodeText, isRoot, isLeaf);

  const fontSize = isRoot ? 16 : isLeaf ? 12 : 13;
  const fontWeight = isRoot ? 700 : isLeaf ? 500 : 600;
  const mctx = Layout.getMeasurementCtx();
  mctx.font = `${fontWeight} ${fontSize}px system-ui, -apple-system, sans-serif`;

  let w;
  let h;
  let wrapped;
  if (dims.width !== null) {
    // Explicit dims are full border-box sizes (the DOM sets them as width/
    // min-height with box-sizing: border-box).
    w = dims.width;
    h = dims.height || Layout.DEFAULT_NODE_HEIGHT;
    const width = Math.max(40, w - 44);
    const lines = Math.max(1, dims.lines);
    wrapped = cachedText(`w|${mctx.font}|${width}|${lines}|${nodeText}`, () => wrapText(mctx, nodeText, width, lines));
  } else {
    // Natural sizing: text width + the CSS padding (10px 22px), title capped
    // at 220px like .mm-title's max-width.
    const textW = Math.min(cachedText(`m|${mctx.font}|${nodeText}`, () => mctx.measureText(nodeText).width), 220);
    w = Math.ceil(textW) + 44;
    h = Math.ceil(fontSize * 1.4) + 20;
    wrapped = [nodeText];
  }

  const rec = {
    id: node.id,
    x,
    y,
    w: isRoutine(node.id) ? footprint.width : w,
    h: isRoutine(node.id) ? footprint.height : h,
    bodyW: w,
    bodyH: h,
    isRoot,
    isLeaf,
    collapsed: !!(hasChildren && node.collapsed),
    color: node.color,
    text: nodeText,
    wrapped,
    routine:isRoutine(node.id),
    status: State.callbacks?.getEffectiveStatus?.(node) || node.status || null,
    spotlight: getSpotlightState(node),
    hasNotes: !isRoot && !!(node.notes && node.notes.trim()),
  };
  sceneNodes.push(rec);
  return rec;
}

// Canvas text layout by font, width and text: every render measured every
// node's text again (20,000 items on a phone: 1.1 s of the 1.9 s to open the
// view, 0.9 s per edit). Same bound as the dimensions memo (layout.js).
const textCache = new Map();
const TEXT_CACHE_MAX = 60000;

function cachedText(key, compute) {
  let value = textCache.get(key);
  if (value === undefined) {
    if (textCache.size >= TEXT_CACHE_MAX) textCache.clear();
    value = compute();
    textCache.set(key, value);
  }
  return value;
}

/** Greedy word-wrap under the CURRENT mctx font, capped at maxLines. */
function wrapText(mctx, text, maxWidth, maxLines) {
  const words = text.split(/\s+/);
  const lines = [];
  let line = "";
  for (const word of words) {
    const candidate = line ? line + " " + word : word;
    if (mctx.measureText(candidate).width <= maxWidth || !line) {
      line = candidate;
    } else {
      lines.push(line);
      line = word;
      if (lines.length >= maxLines) break;
    }
  }
  if (lines.length < maxLines && line) lines.push(line);
  return lines.length ? lines : [text];
}

export function createMmNode(node, x, y, isRoot, direction) {
  const hasChildren = node.children?.length > 0;
  const isLeaf = !hasChildren;

  // Calculate dimensions based on text content
  const nodeText = node.name || node.text || '';
  const dims = Layout.calculateNodeDimensions(nodeText, isRoot, isLeaf);

  const el = document.createElement("div");
  el.className = `mm-node ${isRoot ? "root" : ""} ${isLeaf ? "leaf" : ""}`;

  // Add collapsed class for CSS hover behavior
  if (hasChildren && node.collapsed) {
    el.classList.add("collapsed");
  }

  el.dataset.id = node.id;
  el._mmNode = node;
  const effectiveStatus = State.callbacks?.getEffectiveStatus?.(node) || node.status;
  if (effectiveStatus) {
    el.dataset.status = effectiveStatus;
  }

  if (!isRoot && direction) {
    el.dataset.direction = direction;
    el.classList.add(direction === "left" ? "mm-left" : "mm-right");
  }

  // Build style with optional explicit dimensions
  let styleText = `
    left: ${x}px;
    top: ${y}px;
    transform: translate(-50%, -50%);
    background: ${safeColor(node.color)};
  `;

  // Apply calculated dimensions if text is long enough
  if (dims.width !== null) {
    styleText += `
      width: ${dims.width}px;
      min-height: ${dims.height}px;
    `;
  }

  el.style.cssText = styleText;

  const titleSpan = document.createElement("span");
  titleSpan.className = "mm-title";
  titleSpan.textContent = nodeText.slice(0, 1000); // Enforce 1000 char limit
  titleSpan.title = nodeText;

  // Apply multi-line styling if needed
  if (dims.lines > 1) {
    titleSpan.style.cssText = `
      display: block;
      white-space: pre-wrap;
      word-wrap: break-word;
      overflow-wrap: break-word;
      max-width: none;
      -webkit-line-clamp: unset;
    `;
  } else if (dims.width !== null) {
    // Single line but wider than default
    titleSpan.style.cssText = `
      max-width: none;
      -webkit-line-clamp: unset;
    `;
  }

  // Check read-only status once for this node
  const isViewer = State.callbacks?.isReadOnly?.();

  // Setup title interaction (double-click to edit)
  Interaction.setupTitleInteraction(titleSpan, node, isViewer);
  el.appendChild(titleSpan);

  // Toggle button for nodes with children (shows on hover, always visible when collapsed)
  // Hide for read-only viewers
  if (hasChildren && !isViewer) {
    const toggleBtn = document.createElement("div");
    toggleBtn.className = "mm-toggle";
    toggleBtn.textContent = node.collapsed ? "+" : "-";
    Interaction.setupToggleInteraction(toggleBtn, el, node, (nodeId, updates) => {
      State.callbacks?.onNodeUpdate?.(nodeId, updates);
      setTimeout(() => renderMindmap(), 0);
    });
    el.appendChild(toggleBtn);
  }

  // Reference node badge - shows linked doc indicator (3-state via helper).
  {
    const linked = getLinkedBadge(node);
    if (linked.isLinkedStub) {
      const refBadge = document.createElement("span");
      refBadge.className = `mm-ref-badge ${linked.className}`;
      refBadge.textContent = linked.icon;
      refBadge.title = linked.title;
      if (linked.state !== "loaded") refBadge.classList.add("unavailable");
      el.appendChild(refBadge);
      el.classList.add("reference-node");
      const stubClasses = linkedStubContainerClasses(node);
      if (stubClasses) el.className += ` ${stubClasses}`;
    }
  }

  // Tag spotlight: mark matching nodes so non-matching ones can be dimmed via CSS.
  // `tag-match-ancestor` keeps ancestors of a match un-dimmed so the path
  // from focus down to the matching descendant stays visible.
  const spotlight = getSpotlightState(node);
  if (spotlight === "match") el.classList.add("tag-match");
  else if (spotlight === "ancestor") el.classList.add("tag-match-ancestor");

  // Notes badge - shows if node has notes (skip root)
  if (!isRoot && node.notes && node.notes.trim()) {
    const notesBadge = document.createElement("span");
    notesBadge.className = "mm-notes-badge";
    notesBadge.textContent = "\u{1F5D2}\uFE0F"; // Note emoji
    // Hover preview is desktop-only; mobile users tap the node to open notes.
    if (!isMobile()) {
      setupNotePreviewOnBadge(notesBadge, node.notes, State.callbacks?.getDocId?.(), State.callbacks?.getDek?.());
    }
    Interaction.setupNotesBadgeInteraction(notesBadge, node);
    el.appendChild(notesBadge);
  }

  appendRoutineBadge(el,node.id);
  // Setup node interaction (hover, click, dblclick, contextmenu)
  Interaction.setupNodeInteraction(el, node, isRoot);

  // Setup drag behavior — mousedown-based, no-op on mobile so skip the listener.
  if (!isMobile()) {
    renderCallbacks?.setupDrag?.(el, node);
  }

  return el;
}

// ============================================================
// Breadcrumbs
// ============================================================

/**
 * Render breadcrumbs
 */
function renderBreadcrumbs() {
  const crumbsEl = State.container?.querySelector("#m-crumbs");
  if (!crumbsEl) return;
  renderHorizonControl(State.container, State.currentState?.horizon, State.callbacks?.onHorizonChange, State.currentState?.currentDocId);

  const path = [];
  let current = State.callbacks?.find(State.currentState?.focusId);

  while (current) {
    path.unshift(current);
    current = State.callbacks?.findParent(current.id);
  }

  const escapeHtml = State.callbacks?.escapeHtml || ((s) => s);

  const viewIndicator = `<span class="view-indicator">${viewLabel("mindmap")}</span>`;

  crumbsEl.innerHTML =
    viewIndicator +
    path
      .map((node, i) => {
        const isLast = i === path.length - 1;
        const name = node.name || node.text || t("views.root");
        return `${i > 0 ? '<span class="crumb-sep">\u203A</span>' : ""}
                <div class="crumb ${isLast ? "current" : ""}" data-id="${node.id}">
                  <span class="crumb-dot" style="background:${node.color}"></span>${escapeHtml(name)}
                </div>`;
      })
      .join("");

  crumbsEl.querySelectorAll(".crumb:not(.current)").forEach((crumb) => {
    crumb.addEventListener("click", () => {
      renderCallbacks?.zoomTo?.(crumb.dataset.id);
    });
  });
}
